import type { PlayerState } from './types'

export type NextIndexResult =
  | { type: 'advance'; index: number }
  | { type: 'stop' }
  | { type: 'pending-radio' }

/**
 * Pure function: given the current player state, compute which track should play next.
 * Does not handle repeat-one (caller must check before calling).
 * Does not perform side effects (pendingRadioAdvanceRef, queue truncation — caller's job).
 */
export type NextIndexInput = Pick<
  PlayerState,
  'queue' | 'currentIndex' | 'radioMode' | 'shuffle' | 'repeat'
>

export function getNextIndex(state: NextIndexInput): NextIndexResult {
  const { queue, currentIndex, radioMode, shuffle, repeat } = state

  if (queue.length === 0) return { type: 'stop' }

  if (radioMode) {
    // Interstitials are synthetic inserts that should play regardless of radioGenerated status.
    // Non-interstitial, non-radioGenerated tracks (e.g. manual queues) are skipped.
    const firstPlayableIndex = queue.findIndex(
      (t, i) => i > currentIndex && (t.radioGenerated || t.isInterstitial)
    )
    if (firstPlayableIndex !== -1) return { type: 'advance', index: firstPlayableIndex }
    return { type: 'pending-radio' }
  }

  if (shuffle) {
    return { type: 'advance', index: Math.floor(Math.random() * queue.length) }
  }

  const nextIndex = currentIndex + 1
  if (nextIndex >= queue.length) {
    if (repeat === 'all') return { type: 'advance', index: 0 }
    return { type: 'stop' }
  }
  return { type: 'advance', index: nextIndex }
}
